import cx from 'classnames';
import { useIsMobile } from 'hooks/useIsMobile';
import React from 'react';
import { createPortal } from 'react-dom';

import { useSuspensionBanner } from '~contexts/SuspensionBannerContext';

import styles from './popout.module.scss';

export enum PopoutSide {
	Left = 'left',
	Right = 'right',
}

export enum PopoutWidth {
	Full = 'full',
	FortyPercent = 'fortyPercent',
	FiftyPercent = 'fiftyPercent',
	SeventyFivePercent = 'seventyFivePercent',
}

// Mirrors the `.popoutContainer` / `.backdrop` z-indexes in popout.module.scss.
// A Popout opened from within another Popout (e.g. a modal launched from a
// right-hand panel) must pass a `zIndex` above POPOUT_CONTAINER_Z_INDEX so it
// stacks on top of the underlying panel instead of behind it.
export const POPOUT_CONTAINER_Z_INDEX = 100;
// Keeps an elevated backdrop just beneath its own container while still sitting
// above whatever it needs to dim (e.g. an underlying panel's container).
const ELEVATED_BACKDROP_Z_OFFSET = 5;

interface PopoutProps extends React.HTMLProps<HTMLDivElement> {
	children: React.ReactNode;
	isOpen: boolean;
	onClose: () => void;
	fromSide?: PopoutSide;
	width?: PopoutWidth;
	fullHeight?: boolean;
	/**
	 * Raises the whole drawer (backdrop + container) above the default stacking
	 * order. Use when this Popout is opened on top of another Popout so it does
	 * not render underneath it. Defaults to the SCSS values (container 100).
	 */
	zIndex?: number;
}

export function Popout({
	children,
	isOpen,
	fromSide = PopoutSide.Right,
	fullHeight = false,
	width,
	onClose,
	zIndex,
	style,
	...rest
}: PopoutProps) {
	const { isSuspensionBannerVisible } = useSuspensionBanner();
	const isMobile = useIsMobile();

	const defaultWidth = isMobile ? PopoutWidth.Full : PopoutWidth.FortyPercent;
	const derivedWidth = width ?? defaultWidth;

	const hasElevatedZIndex = zIndex !== undefined;
	const backdropStyle = hasElevatedZIndex
		? { zIndex: zIndex - ELEVATED_BACKDROP_Z_OFFSET }
		: undefined;
	const containerStyle = hasElevatedZIndex ? { ...style, zIndex } : style;

	return createPortal(
		<>
			<div
				data-testid="popout-backdrop"
				className={cx(styles.backdrop, {
					[styles.isVisible]: isOpen,
					[styles.fullHeight]: fullHeight,
					[styles.withBanner]: isSuspensionBannerVisible,
				})}
				style={backdropStyle}
				onClick={onClose}
			/>
			<div
				data-testid="popout-container"
				className={cx(styles.popoutContainer, {
					[styles.isOpen]: isOpen,
					[styles.fullHeight]: fullHeight,

					[styles.fromLeft]: fromSide === PopoutSide.Left,
					[styles.fromRight]: fromSide === PopoutSide.Right,

					[styles.full]: derivedWidth === PopoutWidth.Full,
					[styles.fortyPercent]: derivedWidth === PopoutWidth.FortyPercent,
					[styles.fiftyPercent]: derivedWidth === PopoutWidth.FiftyPercent,
					[styles.seventyFivePercent]:
						derivedWidth === PopoutWidth.SeventyFivePercent,

					[styles.withBanner]: isSuspensionBannerVisible,
				})}
				style={containerStyle}
				{...rest}
			>
				{children}
			</div>
		</>,
		document.body,
	);
}
